import { TrashIcon } from "@heroicons/react/outline";
import { Fragment, useEffect, useMemo, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import {
  cancelAPIRequest,
  classNames,
  getErrorMessages,
  getPeriodClosedError,
  parseTypedClosedDate,
} from "../../Utils";
import PeriodLockBanner from "../../Components/v2/Accounting/PeriodLockBanner";
import PeriodClosedNotice from "../../Components/v2/Accounting/PeriodClosedNotice";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { NumericFormat } from "react-number-format";
import BusinessStatusBanner from "../../Components/v2/Business/BusinessStatusBanner";
import { isBusinessWriteBlocked } from "../../Constants/business";
import {
  clearBusinessPartner,
  clearBusinessPartnerError,
  clearBusinessProduct,
  clearBusinessProductError,
  clearBusinessTax,
  clearBusinessTaxError,
  clearInitAccountStatus,
  clearInvoiceTransaction,
  clearInvoiceTransactionError,
  requestCreateBusinessPartner,
  requestCreateBusinessProduct,
  requestCreateBusinessTax,
  requestCreateInvoiceTransaction,
  requestGetAccountingTransaction,
  requestInitAccount,
  requestListAccount,
  requestListBusiness,
  requestListBusinessPartner,
  requestListBusinessProduct,
  requestListBusinessTax,
  requestListDebitAccount,
  requestUpdateInvoiceTransaction,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useBusinessProductDispatch,
  useBusinessProductState,
  useBusinessTaxDispatch,
  useBusinessTaxState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  usePeriodLock,
} from "../../Context";
import FormLabel from "../../Components/FormLabel";
import Select from "react-select";
import { Button } from "../../Components/Button";
import clsx from "clsx";
import ErrorSelect from "../../Components/ErrorSelect";
import { ModalConfirm } from "../../Components/ModalConfirm";
import Divider from "../../Components/Divider";
import { Alert } from "../../Components/Alert";
import TableSkeleton from "../../Components/TableSkeleton";
import InputSkeleton from "../../Components/InputSkeleton";
import { Modal } from "../../Components/Modal";
import { EmptyBusinessSkeleton } from "../../Components/EmptyBusinessSkeleton";
import EmptyState from "../../Components/EmptyState";
import { productTypes } from "../ProductCreate";
import { salesAccountCategories, taxComputationOptions } from "../../Constants";
import {
  buildEditLineTax,
  buildLeadTax,
  buildPostedTaxes,
  buildSelectedTaxes,
  calculateFinalTotal,
  calculateItemTaxBreakdown,
  WHOLE_RUPIAH,
  describeTaxRow,
  DISCOUNT_OPTIONS,
  handleDiscountChange,
  transformInvoiceItems,
  validateTaxBase,
  senDecimalProps,
} from "../../Utils/AccountingUtil";
import ItemTaxPicker from "../../Components/v2/Accounting/ItemTaxPicker";

const InvoiceCreate = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const businessIdParam = searchParams.get("business");
  const { id: transactionIdParam } = useParams();

  const businessProductDispatch = useBusinessProductDispatch();
  const businessTaxDispatch = useBusinessTaxDispatch();
  const businessTransactionDispatch = useBusinessTransactionDispatch();
  const accountingDispatch = useAccountingDispatch();

  const {
    request: businessPartnerRequest,
    loading: businessLoading,
    businesses,
    businessPartners,
    businessPartnerLoading,
    businessPartnerError,
    newPartner,
    newPartnerLoading,
    newPartnerError,
  } = useBusinessTransactionState();

  const {
    request: businessProductRequest,
    businessProducts,
    businessProductsLoading,
    businessProductsError,
    newProduct,
    newProductLoading,
    newProductError,
  } = useBusinessProductState();

  const {
    request: businessTaxRequest,
    businessTaxes,
    businessTaxesLoading,
    businessTaxesError,
    newTax,
    newTaxLoading,
    newTaxError,
  } = useBusinessTaxState();

  const {
    transaction,
    loading,
    error,
    newInvoiceID,
    newInvoiceError,
    newInvoiceLoading,
    invoiceID,
    submitLoading,
    submitError,
    accounts,
    accountError,
    accountLoading,
    initAccountLoading,
    initAccountStatus,
    debitAccountRequest,
    debitLoading,
    debits,
    debitError,
  } = useAccountingState();

  const { user } = useAuthState();

  // When editing, the loaded transaction's own type is authoritative so a
  // /purchase/edit/:id URL can never flip a sale into a Bill (or vice versa).
  // When creating (no id), the route decides: /purchase/* = Bill.
  const isPurchase = transactionIdParam
    ? transaction?.type === "EXPENSE_PAYABLE"
    : location.pathname.startsWith("/purchase");

  // Every new invoice is saved in whole rupiah; an edit keeps the arithmetic
  // the invoice was posted with, so opening an old one never moves its PPN.
  const taxRounding = transactionIdParam
    ? (transaction?.taxRounding ?? null)
    : WHOLE_RUPIAH;

  // A Bill may only use purchase (PPN Masukan) taxes, a sale only sales ones.
  // Legacy untyped taxes are treated as sales, as they always have been.
  const availableTaxes = useMemo(
    () =>
      (businessTaxes || []).filter((tx) =>
        isPurchase ? tx.type === "PURCHASES" : tx.type !== "PURCHASES",
      ),
    [businessTaxes, isPurchase],
  );

  const [selectedBusiness, setselectedBusiness] = useState(null);
  const [selectedCustomer, setselectedCustomer] = useState(null);
  const [selectedDiscount, setselectedDiscount] = useState(null);
  const [date, setdate] = useState(new Date());
  const [dueDate, setdueDate] = useState(new Date());

  // Only the transaction date is constrained. A due date may legitimately fall
  // inside a closed period — it records no journal entry.
  const {
    minDate: periodMinDate,
    isDateClosed,
    isClosed: periodClosed,
    closedThroughLabel,
    refetch: refetchPeriod,
  } = usePeriodLock(selectedBusiness?.id, { locale: i18n.language });
  const dateLocked = isDateClosed(date);

  const [reference, setreference] = useState("");
  const [description, setdescription] = useState("");
  const [subtractStock, setsubtractStock] = useState(false);
  const [ignoreStockValidation, setignoreStockValidation] = useState(false);

  const [partnerName, setpartnerName] = useState("");
  const [businessType, setbusinessType] = useState("");
  const [discount, setdiscount] = useState("");
  const [applyAfterTax, setapplyAfterTax] = useState(false);
  const [partnerNameError, setpartnerNameError] = useState(null);
  const [partnerTypeError, setpartnerTypeError] = useState(null);
  const [partnerSubmitted, setpartnerSubmitted] = useState(false);

  const [productName, setproductName] = useState("");
  const [productType, setproductType] = useState("");
  const [productPrice, setproductPrice] = useState({ value: null });
  const [productTypeError, setproductTypeError] = useState(null);
  const [productPriceError, setproductPriceError] = useState(null);
  const [productNameError, setproductNameError] = useState(null);
  const [productSubmitted, setproductSubmitted] = useState(false);

  const [taxName, settaxName] = useState("");
  const [computation, setcomputation] = useState("PERCENTAGE");
  const [taxAmount, settaxAmount] = useState({ value: null, floatValue: null });
  const [debitAccount, setdebitAccount] = useState(null);
  const [taxNameError, settaxNameError] = useState(null);
  const [computationError, setcomputationError] = useState(null);
  const [taxAmountError, settaxAmountError] = useState(null);
  const [debitAccountError, setdebitAccountError] = useState(null);
  const [taxSubmitted, settaxSubmitted] = useState(false);

  const [submitted, setsubmitted] = useState(false);

  const [invoiceItems, setinvoiceItems] = useState([
    {
      product: "",
      note: "",
      quantity: 1,
      unit: "",
      price: "",
      tax: "",
      // Always present, so the save always states the line's taxes — leaving
      // the key out tells the backend to keep the ones it already stored.
      selectedTaxes: [],
      amount: "",
      taxAmount: 0,
    },
  ]);

  const [businessError, setbusinessError] = useState("");
  const [customerError, setcustomerError] = useState("");
  const [dateError, setdateError] = useState("");
  const [dueDateError, setdueDateError] = useState("");
  const [discountError, setdiscountError] = useState("");

  const [showSuccessModal, setshowSuccessModal] = useState(false);
  const [showErrorModal, setshowErrorModal] = useState(false);
  const [showPartnerModal, setshowPartnerModal] = useState(false);
  const [showProductModal, setshowProductModal] = useState(false);
  const [showTaxModal, setshowTaxModal] = useState(false);

  const parsedErrorResponse = newInvoiceError?.response?.body
    ? JSON.parse(newInvoiceError.response.body)
    : null;
  const newInvoiceItemsError = parsedErrorResponse?.error?.items;

  const sortAccountCode = (a, b) => {
    const aCode = parseInt(a.code, 10);
    const bCode = parseInt(b.code, 10);
    return (aCode < 100000) - (bCode < 100000) || aCode - bCode;
  };

  const businessTypes = useMemo(() => {
    return [
      { label: "Personal", value: "PERSONAL" },
      { label: "Incorporated", value: "BUSINESS" },
    ].map((item) => ({
      ...item,
      label: t(item.label),
    }));
  }, [t]);

  const taxBaseTypes = useMemo(() => {
    return [
      { label: "decimal", value: "DECIMAL" },
      { label: "percentage", value: "PERCENTAGE" },
      { label: "fraction", value: "FRACTION" },
    ].map((item) => ({
      ...item,
      label: t(item.label),
    }));
  }, [t]);

  const discountType = useMemo(() => {
    return DISCOUNT_OPTIONS.map((item) => ({
      ...item,
      label: t(item.label),
    }));
  }, [t]);

  useEffect(() => {
    const noDiscountOption = DISCOUNT_OPTIONS.find(
      (opt) => opt.value === "noDiscount",
    );
    setselectedDiscount({
      value: noDiscountOption.value,
      label: t(noDiscountOption.label),
    });
  }, [t]);

  useEffect(() => {
    return () => {
      clearInvoiceTransaction(accountingDispatch);
      clearInvoiceTransactionError(accountingDispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (debitAccountRequest) {
      cancelAPIRequest(debitAccountRequest);
    }
    onLoadDebitAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness?.id, transaction?.business?.id]);

  useEffect(() => {
    if (user.id) {
      requestListBusiness(businessTransactionDispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  useEffect(() => {
    if (transaction && Object.keys(transaction).length > 0) {
      if (!transaction.invoiceNumber) {
        navigate(`/accounting-transaction/create?id=${transactionIdParam}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction?.invoiceNumber]);

  useEffect(() => {
    if (businesses.length === 0) return;

    const setBusiness = (business) => {
      setselectedBusiness({
        ...business,
        label: business.name,
        value: business.id,
      });
    };

    if (businessIdParam) {
      const paramBusiness = businesses.find(
        (item) => item.value === businessIdParam,
      );
      if (paramBusiness) setBusiness(paramBusiness);
      return;
    }

    if (transactionIdParam) return;

    const mainBusiness = businesses.find(
      (business) => business.id === user?.mainBusinessID,
    );
    setBusiness(mainBusiness || businesses[0]);
  }, [businessIdParam, transactionIdParam, businesses, user?.mainBusinessID]);

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessIdParam, transactionIdParam, selectedBusiness]);

  useEffect(() => {
    if (selectedBusiness?.value) {
      if (selectedBusiness?.value !== transaction?.businessID) {
        setinvoiceItems([
          {
            product: "",
            note: "",
            quantity: 1,
            unit: "",
            price: "",
            tax: "",
            selectedTaxes: [],
            amount: "",
            taxAmount: 0,
          },
        ]);
        setselectedCustomer(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness]);

  useEffect(() => {
    if (transactionIdParam) {
      requestGetAccountingTransaction(accountingDispatch, {
        id: transactionIdParam,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionIdParam]);

  useEffect(() => {
    if (transaction?.id) {
      const {
        business,
        createdAt,
        partner,
        paymentDueAt,
        items,
        description,
        referenceNumber,
      } = transaction;

      if (business) {
        setselectedBusiness({
          label: business?.name,
          value: business?.id,
        });
      }

      if (partner) {
        setselectedCustomer({
          label: partner?.name,
          value: partner?.id,
        });
      }

      if (items) {
        const mappedItems = items.items.map((item) => {
          const amount = item.price * item.quantity;

          const mapped = {
            id: item.id,
            // The relations are nullable — the closed period guard keeps this
            // invoice intact after its product or tax is deleted — so the ids
            // are carried separately and the save falls back to them.
            productID: item.productID || null,
            product: item.product
              ? { label: item.product.name, value: item.product.id }
              : null,
            note: item.description || "",
            quantity: item.quantity,
            unit: item.unit || "",
            price: { floatValue: item.price, value: item.price },
            // The terms this line was posted with, kept on the item rather than
            // on the tax object so they survive a re-pick of the same tax.
            taxID: item.taxID || null,
            taxComputation: item.taxComputation || null,
            taxRate: item.taxRate ?? null,
            taxAccountID: item.taxAccountID || null,
            // Built from the line's own fields when the tax relation is null,
            // so a deleted PPN does not take the line's DPP with it.
            tax: buildEditLineTax(item, taxBaseTypes),
            // The terms every tax on this line was posted with, written once
            // and never touched by the picker, so removing a tax and adding it
            // back still charges the rate it was posted with.
            postedTaxes: buildPostedTaxes(item),
            selectedTaxes: buildSelectedTaxes(item, availableTaxes),
            amount: { value: amount },
          };

          // One source of truth with the rest of the screen: the posted rate,
          // not whatever the tax record says today.
          return {
            ...mapped,
            taxAmount: {
              value: calculateItemTaxBreakdown(
                mapped,
                0,
                0,
                false,
                null,
                transaction.taxRounding,
              ).totalTax,
            },
          };
        });

        setinvoiceItems(mappedItems);
      }

      if (transaction?.discount) {
        const discountOption = DISCOUNT_OPTIONS.find(
          (opt) =>
            opt.value ===
            (transaction.discount?.type === "PERCENTAGE"
              ? "percentage"
              : "fixed"),
        );

        setselectedDiscount({
          value: discountOption.value,
          label: t(discountOption.label),
        });
        setdiscount(transaction.discount.value);
        setapplyAfterTax(transaction.discount.applyAfterTax || false);
      } else {
        setapplyAfterTax(false);
      }

      setdate(new Date(createdAt));
      setdueDate(new Date(paymentDueAt));
      setdescription(description);
      setreference(referenceNumber);
      // Legacy invoices may have trackInventory: null; fall back to deductInventory.
      setsubtractStock(
        transaction.trackInventory ?? transaction.deductInventory ?? false,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction?.id, t]);

  useEffect(() => {
    if (submitted) {
      const isBusinessValid = Boolean(selectedBusiness);
      const isCustomerValid = Boolean(selectedCustomer);
      const isDateValid = Boolean(date);
      const isDueDateValid = dueDate >= date;
      const isDiscountValid =
        selectedDiscount?.value === "noDiscount" ||
        (["percentage", "fixed"].includes(selectedDiscount?.value) && discount);

      if (isBusinessValid) {
        setErrorMessage("business", "");
      } else {
        setErrorMessage("business", "Business is required.");
      }

      if (isCustomerValid) {
        setErrorMessage("customer", "");
      } else {
        setErrorMessage("customer", "Customer is required.");
      }

      if (isDateValid) {
        setErrorMessage("date", "");
      } else {
        setErrorMessage("date", "Date is required.");
      }

      if (isDueDateValid) {
        setErrorMessage("dueDate", "");
      } else {
        if (dueDate && dueDate < date) {
          setErrorMessage(
            "dueDate",
            "Due date cannot be less than the transaction date.",
          );
        } else {
          setErrorMessage("dueDate", "Due date is required.");
        }
      }
      if (isDiscountValid) {
        setErrorMessage("discount", "");
      } else {
        setErrorMessage("discount", "discountRequired");
      }
    }
  }, [
    selectedBusiness,
    selectedCustomer,
    date,
    dueDate,
    discount,
    selectedDiscount?.value,
    submitted,
  ]);

  useEffect(() => {
    if (partnerSubmitted) {
      const isNameValid = partnerName.length >= 1;
      if (isNameValid) {
        setErrorMessage("partnerName", "");
      } else {
        setErrorMessage("partnerName", "Partner is required");
      }
    }
  }, [partnerName, partnerSubmitted]);

  useEffect(() => {
    if (partnerSubmitted && businessType.value) {
      const isTypeValid = businessTypes.some(
        (type) => type.value === businessType.value,
      );
      if (isTypeValid) {
        setErrorMessage("partnerType", "");
      } else {
        setErrorMessage("partnerType", "Select valid business type.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessType.value, partnerSubmitted]);

  useEffect(() => {
    if (productSubmitted) {
      const isNameValid = productName.length >= 1;
      const isTypeValid = productTypes.some(
        (type) => type.value === productType,
      );
      const isPriceValid = productPrice.value > 0;

      if (isNameValid) {
        setErrorMessage("productName", "");
      } else {
        setErrorMessage("productName", "Product name is required.");
      }

      if (isTypeValid) {
        setErrorMessage("productType", "");
      } else {
        setErrorMessage("productType", "Please select a valid type.");
      }

      if (isPriceValid) {
        setErrorMessage("productPrice", "");
      } else {
        setErrorMessage("productPrice", "Price is required.");
      }
    }
  }, [productName, productSubmitted, productType, productPrice]);

  useEffect(() => {
    if (taxSubmitted) {
      const isNameValid = taxName.length >= 1;
      const isAmountValid = Boolean(taxAmount.value);
      const isComputationValid = taxComputationOptions.some(
        (taxComputation) => taxComputation.value === computation,
      );
      const isDebitValid = Boolean(debitAccount?.value);

      if (isNameValid) {
        setErrorMessage("taxName", "");
      } else {
        setErrorMessage("taxName", "Tax name is required.");
      }

      if (isAmountValid) {
        setErrorMessage("taxAmount", "");
      } else {
        setErrorMessage("taxAmount", "Amount is required.");
      }

      if (isDebitValid) {
        setErrorMessage("debitAccount", "");
      } else {
        setErrorMessage("debitAccount", "Account is required.");
      }

      if (isComputationValid) {
        setErrorMessage("computation", "");
      } else {
        setErrorMessage("computation", "Please select a valid computation.");
      }
    }
  }, [taxName, taxSubmitted, taxAmount, debitAccount, computation]);

  useEffect(() => {
    if (newProduct) {
      setshowSuccessModal(true);
      setshowProductModal(false);
      if (newProduct.id && newProduct.name) {
        const selectedProduct = {
          label: newProduct.name,
          value: newProduct.id,
        };
        handleProductChange(invoiceItems.length - 1, selectedProduct);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newProduct]);

  useEffect(() => {
    if (newPartner) {
      setshowSuccessModal(true);
      setshowPartnerModal(false);
      if (newPartner.id && newPartner.name) {
        setselectedCustomer({
          label: newPartner.name,
          value: newPartner.id,
        });
      }
    }
  }, [newPartner]);

  useEffect(() => {
    if (newTax) {
      setshowSuccessModal(true);
      setshowTaxModal(false);
    }
  }, [newTax]);

  useEffect(() => {
    if (newInvoiceID) {
      setshowSuccessModal(true);
    } else if (newInvoiceError) {
      setshowErrorModal(true);
    }
  }, [newInvoiceID, newInvoiceError]);

  useEffect(() => {
    if (invoiceID) {
      setshowSuccessModal(true);
    } else if (submitError) {
      setshowErrorModal(true);
    }
  }, [invoiceID, submitError]);

  useEffect(() => {
    if (debits.length > 0) {
      const selectedDebit = debits.find((debit) => debit.code === "220500");
      if (selectedDebit) {
        setdebitAccount({
          ...selectedDebit,
          label: selectedDebit.name,
          value: selectedDebit.id,
        });
      } else {
        setdebitAccount(null);
      }
    }
  }, [debits, showTaxModal]);

  useEffect(() => {
    if (selectedBusiness?.id) {
      requestListAccount(accountingDispatch, {
        businessID: selectedBusiness.id,
      });
    } else {
      if (user.id) {
        requestListBusiness(accountingDispatch, {
          owner: user.id,
          page: 1,
          limit: 10,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness?.id, user.id]);

  useEffect(() => {
    if (initAccountStatus === "SUCCESS") {
      requestListAccount(accountingDispatch, {
        businessID: selectedBusiness.id,
      });
      clearInitAccountStatus(accountingDispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initAccountStatus]);

  const onLoad = () => {
    if (businessPartnerRequest) {
      cancelAPIRequest(businessProductRequest);
    }
    if (businessProductRequest) {
      cancelAPIRequest(businessProductRequest);
    }
    if (businessTaxRequest) {
      cancelAPIRequest(businessTaxRequest);
    }

    if (selectedBusiness?.value || businessIdParam) {
      requestListBusinessProduct(businessProductDispatch, {
        businessID: selectedBusiness?.value || businessIdParam,
      });
      requestListBusinessTax(businessTaxDispatch, {
        businessID: selectedBusiness?.value || businessIdParam,
      });
      requestListBusinessPartner(businessTransactionDispatch, {
        businessID: selectedBusiness?.value || businessIdParam,
      });
    }
  };

  const validateInvoiceItems = (items) =>
    items.every((item) => {
      // A line whose product the guard hides keeps its productID and the save
      // keeps it too, so it counts as filled in even with no product object.
      const isProductValid =
        Boolean(item.productID) ||
        (item.product !== null && item.product !== "");
      const isQuantityValid = item.quantity !== 0;
      const isPriceValid = item.price !== null && item.price !== "";

      let isTaxBaseValid = true;
      if (item.tax?.taxBaseType) {
        switch (item.tax.taxBaseType.value) {
          case "DECIMAL":
            isTaxBaseValid = !!item.tax.taxBaseRate;
            break;
          case "PERCENTAGE":
            isTaxBaseValid = !!item.tax.taxBasePercentage;
            break;
          case "FRACTION":
            isTaxBaseValid =
              !!item.tax.taxBaseFractionNumerator &&
              !!item.tax.taxBaseFractionDenumerator;
            break;
          default:
            isTaxBaseValid = true;
            break;
        }
      }

      return (
        isProductValid && isQuantityValid && isPriceValid && isTaxBaseValid
      );
    });

  const validateInput = () => {
    let currentValidBusiness = true;
    let currentValidCustomer = true;
    let currentValidDate = true;
    let currentValidDueDate = true;
    let currentValidDiscount = true;

    const isBusinessValid = Boolean(selectedBusiness);
    const isCustomerValid = Boolean(selectedCustomer);
    const isDateValid = Boolean(date);
    const isDueDateValid = dueDate >= date;
    const isDiscountValid =
      selectedDiscount?.value === "noDiscount" ||
      (["percentage", "fixed"].includes(selectedDiscount?.value) && discount);

    currentValidBusiness = isBusinessValid;
    currentValidCustomer = isCustomerValid;
    currentValidDate = isDateValid;
    currentValidDueDate = isDueDateValid;
    currentValidDiscount = isDiscountValid;

    return (
      currentValidBusiness &&
      currentValidCustomer &&
      currentValidDate &&
      currentValidDueDate &&
      currentValidDiscount
    );
  };

  const validatePartnerForm = () => {
    let currentValidPartnerName = true;
    let currentValidType = true;

    const isNameValid = partnerName.length >= 1;
    const isTypeValid = businessTypes.some(
      (type) => type.value === businessType.value,
    );

    currentValidPartnerName = isNameValid;

    if (businessType) {
      currentValidType = isTypeValid;
    }

    return currentValidPartnerName && currentValidType;
  };

  const validateProductForm = () => {
    let currentValidProductName = true;
    let currentValidProductType = true;
    let currentValidProductPrice = true;

    const isNameValid = productName.length >= 1;
    const isTypeValid = productTypes.some((type) => type.value === productType);
    const isPriceValid = productPrice.value > 0;

    currentValidProductName = isNameValid;
    currentValidProductType = isTypeValid;
    currentValidProductPrice = isPriceValid;

    return (
      currentValidProductName &&
      currentValidProductType &&
      currentValidProductPrice
    );
  };

  const validateTaxForm = () => {
    let currentValidTaxName = true;
    let currentValidTaxAmount = true;
    let currentValidComputation = true;
    let currentValidDebitAccount = true;

    const isNameValid = taxName.length >= 1;
    const isAmountValid = Boolean(taxAmount.value);
    const isComputationValid = taxComputationOptions.some(
      (taxComputation) => taxComputation.value === computation,
    );
    const isDebitValid = Boolean(debitAccount?.value);

    currentValidTaxName = isNameValid;
    currentValidTaxAmount = isAmountValid;
    currentValidComputation = isComputationValid;
    currentValidDebitAccount = isDebitValid;

    return (
      currentValidTaxName &&
      currentValidTaxAmount &&
      currentValidComputation &&
      currentValidDebitAccount
    );
  };

  const onSave = (update = false) => {
    setsubmitted(true);
    if (dateLocked) return;

    const valid = validateInput();
    const itemsValid = validateInvoiceItems(invoiceItems);

    if (valid && itemsValid) {
      const items = transformInvoiceItems(invoiceItems);

      const input = {
        business_id: selectedBusiness.value,
        created_at: date.toISOString(),
        partner_id: selectedCustomer?.value,
        payment_due_at: dueDate?.toISOString(),
        items,
        type: isPurchase ? "EXPENSE_PAYABLE" : "INCOME_RECEIVABLE",
        description: description || "",
        reference_number: reference || "",
        track_inventory: subtractStock,
        ...(!transactionIdParam && {
          ignore_low_stock: ignoreStockValidation,
        }),
        ...(discount && {
          discount: {
            type:
              selectedDiscount.value === "percentage" ? "PERCENTAGE" : "AMOUNT",
            value: parseFloat(discount),
            applyAfterTax,
          },
        }),
      };

      if (update) {
        input.id = transaction.id;
        requestUpdateInvoiceTransaction(accountingDispatch, { input });
      } else {
        requestCreateInvoiceTransaction(accountingDispatch, { input });
      }
    }
  };

  const handlePartnerSave = () => {
    setpartnerSubmitted(true);
    if (validatePartnerForm()) {
      const input = {
        businessID: selectedBusiness.value,
        name: partnerName,
        type: businessType.value,
      };
      requestCreateBusinessPartner(businessTransactionDispatch, { input });
    }
  };

  const handleProductSave = () => {
    setproductSubmitted(true);
    if (validateProductForm()) {
      const input = {
        businessID: selectedBusiness.value,
        name: productName,
        type: productType,
        price: productPrice.value,
      };
      requestCreateBusinessProduct(businessProductDispatch, { input });
    }
  };

  const handleTaxSave = () => {
    settaxSubmitted(true);
    if (validateTaxForm()) {
      const input = {
        businessID: selectedBusiness.value,
        name: taxName,
        computation,
        type: isPurchase ? "PURCHASES" : "SALES",
        amount: taxAmount.value,
        accountID: debitAccount.value,
      };
      requestCreateBusinessTax(businessTaxDispatch, { input });
    }
  };

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "business":
        setbusinessError(message);
        break;
      case "customer":
        setcustomerError(message);
        break;
      case "date":
        setdateError(message);
        break;
      case "dueDate":
        setdueDateError(message);
        break;
      case "partnerName":
        setpartnerNameError(message);
        break;
      case "partnerType":
        setpartnerTypeError(message);
        break;
      case "productName":
        setproductNameError(message);
        break;
      case "productType":
        setproductTypeError(message);
        break;
      case "productPrice":
        setproductPriceError(message);
        break;
      case "taxName":
        settaxNameError(message);
        break;
      case "computation":
        setcomputationError(message);
        break;
      case "taxAmount":
        settaxAmountError(message);
        break;
      case "debitAccount":
        setdebitAccountError(message);
        break;
      case "discount":
        setdiscountError(message);
        break;
      default:
        break;
    }
  };

  const handleAddRow = () => {
    if (invoiceItems.length < 100) {
      setinvoiceItems([
        ...invoiceItems,
        {
          product: "",
          note: "",
          quantity: 1,
          unit: "",
          price: "",
          tax: "",
          selectedTaxes: [],
          amount: "",
          taxAmount: 0,
        },
      ]);
    }
  };

  const handleRemoveRow = (index) => {
    const newInvoiceItems = [...invoiceItems];
    newInvoiceItems.splice(index, 1);
    setinvoiceItems(newInvoiceItems);
  };

  const updateInvoiceItem = (index, updatedProperties) => {
    return (prevState) => {
      const updatedItems = [...prevState];
      updatedItems[index] = {
        ...updatedItems[index],
        ...updatedProperties,
      };
      return updatedItems;
    };
  };

  const handleProductChange = (index, selectedOption) => {
    if (selectedOption) {
      setinvoiceItems((prevState) => {
        const updatedItems = [...prevState];
        const product = selectedOption;
        const { tax } = updatedItems[index];

        // Handle set tax base based on product
        if (tax?.value) {
          let selectedTaxBase;
          if (product.taxBaseType) {
            selectedTaxBase = taxBaseTypes.find(
              (type) => type.value === product.taxBaseType,
            );
          }
          if (selectedTaxBase?.value === "DECIMAL" && product.taxBaseRate) {
            tax.taxBaseType = selectedTaxBase;
            tax.taxBaseRate = product.taxBaseRate;
          }
          if (
            selectedTaxBase?.value === "PERCENTAGE" &&
            product.taxBasePercentage
          ) {
            tax.taxBaseType = selectedTaxBase;
            tax.taxBasePercentage = product.taxBasePercentage;
          }
          if (
            selectedTaxBase?.value === "FRACTION" &&
            product.taxBaseFractionNumerator &&
            product.taxBaseFractionDenumerator
          ) {
            tax.taxBaseType = selectedTaxBase;
            tax.taxBaseFractionNumerator = product.taxBaseFractionNumerator;
            tax.taxBaseFractionDenumerator = product.taxBaseFractionDenumerator;
          }
        }
        updatedItems[index] = {
          ...updatedItems[index],
          product,
          tax,
          price: {
            value: product.price || null,
          },
        };
        return updatedItems;
      });
    }
  };

  const handleQuantityChange = (e, index) => {
    const newValue = Number(e.target.value);
    if (!isNaN(newValue) && newValue >= 0 && newValue <= 999) {
      setinvoiceItems(
        updateInvoiceItem(index, {
          quantity: newValue,
        }),
      );
    }
  };

  // A line can carry several taxes now, so the picker hands back the whole
  // list. The tax base still belongs to one of them — the lead tax, the first
  // that is not withheld — because it is a property of what is being sold, not
  // of each tax charged on it.
  //
  // Nothing here writes taxID or the frozen terms. Those describe what the
  // line was POSTED with, and `postedTaxes` keeps them whatever the user picks,
  // so removing a tax and adding it back still charges the posted rate.
  const handleTaxesChange = (index, next) => {
    setinvoiceItems((prevState) => {
      const updatedItems = [...prevState];
      const current = updatedItems[index];
      const selectedTaxes = next || [];
      const tax = buildLeadTax(current, selectedTaxes, taxBaseTypes);

      updatedItems[index] = { ...current, selectedTaxes, tax };
      return updatedItems;
    });
  };

  const onSelectDiscont = (option) => {
    setselectedDiscount(option);
    setdiscount("");
  };

  const handleChangeDiscount = (e) => {
    handleDiscountChange(e.target.value, selectedDiscount, setdiscount);
  };

  // Names only. A line that carries a tax the picker filters out — a sales
  // tax on a bill, or one that was deleted — still charges the terms it was
  // posted with; this just stops the summary calling it "Pajak tidak
  // tersedia" while the tax is sitting in the business's list.
  const taxDirectory = useMemo(
    () => new Map((businessTaxes || []).map((tax) => [tax.id, tax])),
    [businessTaxes],
  );

  // Update the total calculations
  const calculations = useMemo(() => {
    return calculateFinalTotal(
      invoiceItems,
      selectedDiscount?.value,
      discount,
      applyAfterTax,
      taxDirectory,
      taxRounding,
    );
  }, [
    invoiceItems,
    selectedDiscount,
    discount,
    applyAfterTax,
    taxDirectory,
    taxRounding,
  ]);

  // Replace the current subtotal calculation
  const taxes = calculations.taxes;
  const subtotal = calculations.subtotal;
  const discountAmount = calculations.discount;
  const totalWithTax = calculations.grandTotal;
  const roundingAmount = calculations.roundingAmount;

  const navigateToTransaction = () => {
    clearInvoiceTransaction(accountingDispatch);
    clearInvoiceTransactionError(accountingDispatch);
    setshowSuccessModal(false);
    if (newInvoiceID || invoiceID) {
      navigate("/v2/accounting");
    }
  };

  const resetPartnerModal = () => {
    setpartnerName("");
    setbusinessType("");
    setpartnerNameError("");
    setpartnerTypeError("");
  };

  const resetProductModal = () => {
    setproductName("");
    setproductType("");
    setproductPrice("");
    setproductNameError("");
    setproductTypeError("");
    setproductPriceError("");
  };

  const resetTaxModal = () => {
    settaxName("");
    settaxAmount("");
    setcomputation("");

    settaxNameError("");
    settaxAmountError("");
    setcomputationError("");
    setdebitAccountError("");
  };

  const handleInitAccount = () => {
    requestInitAccount(accountingDispatch, {
      businessID: selectedBusiness?.id,
    });
  };

  const onLoadDebitAccount = () => {
    if (selectedBusiness?.id || businessIdParam || transaction?.business?.id) {
      const accountCategories = salesAccountCategories;
      requestListDebitAccount(accountingDispatch, {
        businessID:
          selectedBusiness?.id || businessIdParam || transaction?.business?.id,
        categories: accountCategories,
      });
    }
  };

  const renderDebitAccount = () => {
    return (
      <>
        {debitError ? (
          <ErrorSelect
            name="account"
            label="Account"
            error={debitError}
            onRefresh={onLoadDebitAccount}
          />
        ) : (
          <div>
            <FormLabel htmlFor="account" label={t("Account")} required />
            <Select
              name="account"
              className={classNames(
                debitError || debitAccountError
                  ? "border-red-500 rounded border focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 text-sm",
              )}
              classNamePrefix="select2-selection"
              value={debitAccount}
              onChange={setdebitAccount}
              options={debits
                .slice()
                .sort(sortAccountCode)
                .map((obj) => ({
                  value: obj.id,
                  label: `${obj.code} - ${obj.name}`,
                }))}
              placeholder={t("Choose Account")}
              isDisabled={debitLoading}
              isLoading={debitLoading}
            />
            {debitAccountError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(debitAccountError)}
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  const renderPartnerModal = () => {
    return (
      <Modal
        title={t("Add Partner")}
        show={showPartnerModal}
        onClose={() => {
          setshowPartnerModal(false);
          setpartnerSubmitted(false);
          resetPartnerModal();
          clearBusinessPartnerError(businessTransactionDispatch);
        }}
        footer={
          <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg">
            <Button
              disabled={newPartnerLoading}
              loading={newPartnerLoading}
              onClick={(e) => {
                e.preventDefault();
                handlePartnerSave();
              }}
              className="w-24 shrink-0"
              title="Save"
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4 px-4 py-5">
          {newPartnerError && (
            <Alert
              title="Error"
              type="error"
              message={
                newPartnerError?.message ||
                "newPartnerError delete Transaction."
              }
              errors={newPartnerError?.errors}
            />
          )}
          <div>
            <FormLabel htmlFor="name" label={t("Name")} required />
            <input
              type="text"
              name="name"
              value={partnerName}
              placeholder={t("Enter Partner Name")}
              onChange={(e) => setpartnerName(e.target.value)}
              className={classNames(
                partnerNameError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm",
              )}
            />
            {partnerNameError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(partnerNameError)}
              </div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="type" label={t("Business Type")} />
            <Select
              id="type"
              name="type"
              className={classNames(
                partnerTypeError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 text-sm",
              )}
              classNamePrefix="select2-selection"
              options={businessTypes}
              value={businessType}
              onChange={setbusinessType}
              placeholder={t("Select Business Type")}
            />
            {partnerTypeError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(partnerTypeError)}
              </div>
            )}
          </div>
        </div>
      </Modal>
    );
  };

  const renderProductModal = () => {
    return (
      <Modal
        title={t("Create Product")}
        show={showProductModal}
        onClose={() => {
          setshowProductModal(false);
          setproductSubmitted(false);
          resetProductModal();
          clearBusinessProductError(businessProductDispatch);
          handleRemoveRow(invoiceItems.length - 1);
        }}
        footer={
          <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg">
            <Button
              disabled={newProductLoading}
              loading={newProductLoading}
              onClick={(e) => {
                e.preventDefault();
                handleProductSave();
              }}
              className="w-24 shrink-0"
              title="Save"
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4 px-4 py-5">
          {newProductError && (
            <Alert
              title="Error"
              type="error"
              message={
                newProductError?.message || "newProductError Transaction."
              }
              errors={newProductError?.errors}
            />
          )}
          <div>
            <FormLabel htmlFor="name" label={t("Name")} required />
            <input
              type="text"
              name="name"
              value={productName}
              placeholder={t("Enter Product Name")}
              maxLength={120}
              onChange={(e) => setproductName(e.target.value)}
              className={classNames(
                productNameError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm",
              )}
            />
            {productNameError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(productNameError)}
              </div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="type" label={t("Type")} required />
            <select
              id="type"
              name="type"
              value={productType}
              onChange={(e) => setproductType(e.target.value)}
              className={classNames(
                productTypeError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm",
              )}
            >
              {productTypes.map((productType) => (
                <option key={productType.value} value={productType.value}>
                  {t(productType.label)}
                </option>
              ))}
            </select>
            {productTypeError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(productTypeError)}
              </div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="price" label={t("Price")} required />
            <NumericFormat
              type="text"
              name="price"
              id="price"
              value={productPrice.value || ""}
              placeholder={t("Enter Price")}
              thousandSeparator="."
              decimalSeparator=","
              displayType="input"
              prefix={"Rp"}
              onValueChange={(values) => setproductPrice(values)}
              className={classNames(
                productPriceError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm",
              )}
            />
            {productPriceError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(productPriceError)}
              </div>
            )}
          </div>
        </div>
      </Modal>
    );
  };

  const renderTaxModal = () => {
    return (
      <Modal
        title={t("Create Tax")}
        show={showTaxModal}
        onClose={() => {
          setshowTaxModal(false);
          settaxSubmitted(false);
          resetTaxModal();
          clearBusinessTaxError(businessTaxDispatch);
        }}
        footer={
          <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg">
            <Button
              disabled={newTaxLoading}
              loading={newTaxLoading}
              onClick={(e) => {
                e.preventDefault();
                handleTaxSave();
              }}
              className="w-24 shrink-0"
              title="Save"
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4 px-4 py-5">
          {newTaxError && (
            <Alert
              title="Error"
              type="error"
              message={newTaxError?.message || "newTaxError Transaction."}
              errors={newTaxError?.errors}
            />
          )}
          <div>
            <FormLabel htmlFor="name" label={t("Name")} required />
            <input
              type="text"
              name="name"
              value={taxName}
              placeholder={t("Enter Tax Name")}
              maxLength={120}
              onChange={(e) => settaxName(e.target.value)}
              className={classNames(
                taxNameError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm",
              )}
            />
            {taxNameError && (
              <div className="mt-1 text-red-600 text-sm">{t(taxNameError)}</div>
            )}
          </div>
          <div>
            <FormLabel
              htmlFor="computation"
              label={t("Computation")}
              required
            />
            <select
              id="computation"
              name="computation"
              value={computation}
              onChange={(e) => setcomputation(e.target.value)}
              className={classNames(
                computationError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm",
              )}
            >
              {taxComputationOptions.map((taxComputation) => (
                <option key={taxComputation.value} value={taxComputation.value}>
                  {t(taxComputation.label)}
                </option>
              ))}
            </select>
            {computationError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(computationError)}
              </div>
            )}
          </div>
          <div>
            {selectedBusiness?.id && accounts.length === 0 ? (
              accountLoading ? (
                <InputSkeleton rows={1} />
              ) : accountError ? (
                <>
                  <FormLabel htmlFor="account" label={t("Account")} required />
                  <Alert
                    title="Error"
                    type="error"
                    message={
                      accountError?.message || "Error fetch accounting account."
                    }
                    errors={accountError?.errors}
                  />
                </>
              ) : (
                <>
                  <FormLabel htmlFor="account" label={t("Account")} required />
                  <EmptyAccount />
                </>
              )
            ) : (
              renderDebitAccount()
            )}
          </div>
          <div>
            <FormLabel htmlFor="amount" label={t("Amount")} required />
            <NumericFormat
              type="text"
              name="amount"
              id="amount"
              placeholder={t("Enter Amount")}
              thousandSeparator={computation === "FIXED" ? "." : ""}
              decimalSeparator=","
              displayType="input"
              prefix={computation === "FIXED" ? "Rp" : ""}
              suffix={computation === "FIXED" ? "" : "%"}
              onValueChange={(values) => settaxAmount(values)}
              decimalScale={computation === "FIXED" ? 0 : 2}
              className={classNames(
                taxAmountError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm",
              )}
            />
            {taxAmountError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(taxAmountError)}
              </div>
            )}
          </div>
        </div>
      </Modal>
    );
  };

  const EmptyAccount = () => {
    return initAccountLoading ? (
      <EmptyBusinessSkeleton />
    ) : accountError ? (
      <div className="bg-white shadow-sm max-w-7xl mx-auto overflow-hidden px-4 py-6 sm:px-6 rounded-t-lg">
        <Alert
          title="Error"
          type="error"
          message={accountError?.message || "Error fetch accounting account."}
          errors={accountError?.errors}
        />
      </div>
    ) : (
      <EmptyState
        titleKey="No Accounts"
        descriptionKey="Please create an account first"
      >
        <div className="flex flex-col sm:flex-row justify-between gap-4 sm:gap-6">
          <Button
            outline
            className="flex-1"
            onClick={handleInitAccount}
            title="Create Automatically"
          />

          <Button
            outline
            className="flex-1"
            onClick={() =>
              navigate(`/account/import?id=${selectedBusiness.id}`)
            }
            title="Import Using XLSX File"
          />
        </div>
      </EmptyState>
    );
  };

  const renderSkeletonLoading = () => {
    return (
      <div className="space-y-6 px-4 py-5 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <InputSkeleton rows={4} colSpan={1} />
          <InputSkeleton className="sm:col-end-3" rows={1} colSpan={1} />
        </div>
        <div className="flex flex-col">
          <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Product")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Quantity")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Price")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Subtotal")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Taxes")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("DPP")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("totalTax")}
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <TableSkeleton rows={3} columns={8} />
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 justify-between">
          <InputSkeleton rows={1} colSpan={2} />
          <div className="flex flex-col gap-1 col-span-2 sm:col-span-1 text-sm sm:col-end-5">
            <div className="flex justify-between font-medium">
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
            </div>
            <div className="flex justify-between">
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
            </div>
            <Divider />
            <div className="flex justify-between font-medium">
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTaxBaseForm = (item, index) => {
    const isTaxBaseValid = validateTaxBase(item);
    if (item.tax.taxBaseType.value === "FRACTION") {
      return (
        <div className="flex gap-2">
          <input
            type="number"
            name="taxBaseFractionNumerator"
            min="1"
            value={item.tax.taxBaseFractionNumerator || ""}
            placeholder={t("numerator")}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              if (isNaN(value)) return;
              if (value < 0) return;
              setinvoiceItems(
                updateInvoiceItem(index, {
                  tax: {
                    ...item.tax,
                    taxBaseFractionNumerator: value,
                  },
                }),
              );
            }}
            className={classNames(
              !isTaxBaseValid && submitted
                ? "border-red-500 rounded border focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
              "block w-full text-sm rounded-md shadow-sm",
            )}
          />
          <span className="flex items-center">/</span>
          <input
            type="number"
            name="taxBaseFractionDenumerator"
            value={item.tax.taxBaseFractionDenumerator || ""}
            placeholder={t("denominator")}
            min="1"
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              if (isNaN(value)) return;
              if (value < 0) return;
              setinvoiceItems(
                updateInvoiceItem(index, {
                  tax: {
                    ...item.tax,
                    taxBaseFractionDenumerator: value,
                  },
                }),
              );
            }}
            className={classNames(
              !isTaxBaseValid && submitted
                ? "border-red-500 rounded border focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
              "block w-full text-sm rounded-md shadow-sm",
            )}
          />
        </div>
      );
    }

    if (item.tax.taxBaseType.value === "PERCENTAGE") {
      return (
        <NumericFormat
          type="text"
          name="taxBasePercentage"
          value={item.tax.taxBasePercentage || ""}
          placeholder={t("enterRate")}
          thousandSeparator="."
          decimalSeparator=","
          displayType="input"
          suffix="%"
          onValueChange={(values) => {
            const value = Math.max(0, values.floatValue || 0);
            setinvoiceItems(
              updateInvoiceItem(index, {
                tax: {
                  ...item.tax,
                  taxBasePercentage: value,
                },
              }),
            );
          }}
          isAllowed={(values) => values.floatValue >= 0}
          decimalScale={2}
          className={classNames(
            !isTaxBaseValid && submitted
              ? "border-red-500 rounded border focus:border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
            "block w-full text-sm rounded-md shadow-sm",
          )}
        />
      );
    }

    return (
      <input
        type="number"
        name="taxBaseRate"
        value={item.tax.taxBaseRate || ""}
        placeholder={t("enterRate")}
        onChange={(e) => {
          const value = parseFloat(e.target.value);
          if (isNaN(value)) return;
          if (value < 0) return;
          setinvoiceItems(
            updateInvoiceItem(index, {
              tax: {
                ...item.tax,
                taxBaseRate: value,
              },
            }),
          );
        }}
        className={classNames(
          !isTaxBaseValid && submitted
            ? "border-red-500 rounded border focus:border-red-500 focus:ring-red-500"
            : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
          "block w-full text-sm rounded-md shadow-sm",
        )}
      />
    );
  };

  if (accountLoading) {
    return <EmptyBusinessSkeleton />;
  }

  if (accounts.length === 0 && selectedBusiness?.id && !transaction?.id) {
    return <EmptyAccount />;
  }

  if (!transaction || error) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={error ? getErrorMessages(error) : "Invoice not found."}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-white border border-gray-200 divide-y divide-gray-200 shadow sm:overflow-hidden rounded-md sm:rounded-lg sm:min-h-[30rem]">
        {renderPartnerModal()}
        {renderProductModal()}
        {renderTaxModal()}
        {newTax && (
          <ModalConfirm
            open={showSuccessModal}
            setOpen={(open) => {
              setshowSuccessModal(open);
            }}
            title="Success"
            description={"Your new tax has been created."}
            type="SUCCESS"
            onConfirm={() => {
              setshowTaxModal(false);
              setshowSuccessModal(false);
              clearBusinessTax(businessTaxDispatch);
              settaxSubmitted(false);
              resetTaxModal();
            }}
          />
        )}
        {newProduct && (
          <ModalConfirm
            open={showSuccessModal}
            setOpen={(open) => {
              setshowSuccessModal(open);
            }}
            title="Success"
            description={"Your new product has been created."}
            type="SUCCESS"
            onConfirm={() => {
              setshowProductModal(false);
              setshowSuccessModal(false);
              clearBusinessProduct(businessProductDispatch);
              setproductSubmitted(false);
              resetProductModal();
            }}
          />
        )}
        {newPartner && (
          <ModalConfirm
            open={showSuccessModal}
            setOpen={(open) => {
              setshowSuccessModal(open);
            }}
            title="Success"
            description={"Your new partner has been created."}
            type="SUCCESS"
            onConfirm={() => {
              setshowPartnerModal(false);
              setshowSuccessModal(false);
              clearBusinessPartner(businessTransactionDispatch);
              setpartnerSubmitted(false);
              resetPartnerModal();
            }}
          />
        )}
        {(newInvoiceID || invoiceID) && (
          <ModalConfirm
            open={showSuccessModal}
            setOpen={(open) => {
              setshowSuccessModal(open);
            }}
            title="Success"
            description={
              newInvoiceID
                ? "Your invoice has been created."
                : "Your invoice has been updated."
            }
            type="SUCCESS"
            onConfirm={navigateToTransaction}
          />
        )}
        {(newInvoiceError ||
          (submitError && !getPeriodClosedError(submitError))) && (
          <ModalConfirm
            open={showErrorModal}
            type="ALERT"
            setOpen={(open) => {
              setshowErrorModal(open);
            }}
            title="Error"
            description={getErrorMessages(newInvoiceError || submitError)}
            onConfirm={() => {
              setshowErrorModal(false);
            }}
          />
        )}
        {loading ? (
          renderSkeletonLoading()
        ) : (
          <div className="space-y-6 px-4 py-5 sm:p-6">
            {selectedBusiness?.id && (
              <BusinessStatusBanner business={selectedBusiness} />
            )}
            <PeriodLockBanner businessID={selectedBusiness?.id} />
            {/* Inline and amber rather than the red ALERT modal above — a closed
                period is a status, and the user should stay on their form. */}
            <PeriodClosedNotice error={submitError} onRefresh={refetchPeriod} />
            <form>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <FormLabel
                    htmlFor="business"
                    label={t("Business Name")}
                    required
                  />
                  <Select
                    name="business"
                    className="mt-2 text-sm"
                    classNamePrefix="select2-selection"
                    options={businesses}
                    isLoading={businessLoading}
                    isDisabled={businessLoading || businesses.length === 0}
                    placeholder={
                      businesses.length === 0
                        ? t("No Business Profile Yet")
                        : t("Select Business Name")
                    }
                    value={selectedBusiness || ""}
                    onChange={setselectedBusiness}
                  />
                  {businessError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(businessError)}
                    </div>
                  )}
                </div>
                <div className="w-full">
                  <FormLabel
                    htmlFor="date"
                    label={t("Transaction Date")}
                    required
                  />
                  <div className="relative flex mt-1">
                    <ReactDatePicker
                      className={classNames(
                        dateError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "mt-1 block w-full text-sm rounded-md shadow-sm",
                      )}
                      id="date"
                      selected={date}
                      onChange={setdate}
                      placeholderText={t("Select Transaction Date")}
                      dateFormat="dd/MM/yyyy"
                      minDate={periodMinDate}
                      // A typed closed date is kept so the hint below names the period;
                      // the picker alone would drop it silently.
                      onChangeRaw={(event) => {
                        const typed = parseTypedClosedDate(
                          event?.target?.value,
                          "dd/MM/yyyy",
                          isDateClosed,
                        );
                        if (typed) setdate(typed);
                      }}
                    />
                  </div>
                  {periodClosed && (
                    <p className="mt-1 text-xs text-amber-700">
                      {dateLocked
                        ? t("accounting.period.hints.dateLocked", {
                            date: closedThroughLabel,
                          })
                        : t("accounting.period.banner.closedThrough", {
                            date: closedThroughLabel,
                          })}
                    </p>
                  )}
                  {dateError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(dateError)}
                    </div>
                  )}
                </div>
                {businessPartnerError ? (
                  <ErrorSelect
                    name="partner"
                    label={
                      isPurchase ? "Partner or Supplier" : "Partner or Customer"
                    }
                    error={businessPartnerError}
                    onRefresh={() => {
                      if (businessIdParam) {
                        requestListBusinessPartner(
                          businessTransactionDispatch,
                          {
                            page: 1,
                            businessID: businessIdParam,
                          },
                        );
                      }
                    }}
                  />
                ) : (
                  <div>
                    <div className="flex justify-between">
                      <FormLabel
                        htmlFor="customer"
                        label={t(isPurchase ? "Supplier" : "Customer")}
                        required={true}
                      />
                      <button
                        type="button"
                        onClick={() => setshowPartnerModal(true)}
                        className="text-sm font-medium text-blue-600"
                      >
                        {t("Add Partner")}
                      </button>
                    </div>
                    <Select
                      id="customer"
                      className={classNames(
                        customerError
                          ? "border-red-500 rounded border focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "mt-2 text-sm",
                      )}
                      classNamePrefix="select2-selection"
                      options={businessPartners.map((item) => ({
                        value: item.id,
                        label: item.name,
                      }))}
                      value={selectedCustomer}
                      onChange={(selectedOption) => {
                        setselectedCustomer(selectedOption);
                      }}
                      placeholder={t("Select Partner or Customer")}
                      maxMenuHeight={150}
                      menuPortalTarget={document.body}
                      isLoading={businessPartnerLoading}
                      isDisabled={businessPartnerLoading}
                    />
                    {customerError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {t(customerError)}
                      </div>
                    )}
                  </div>
                )}
                <div className="w-full">
                  <FormLabel htmlFor="dueDate" label={t("Due Date")} required />
                  <div className="relative flex mt-1">
                    <ReactDatePicker
                      className={classNames(
                        dueDateError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "mt-1 block w-full text-sm rounded-md shadow-sm",
                      )}
                      id="dueDate"
                      selected={dueDate}
                      onChange={setdueDate}
                      placeholderText={t("Select Due Date")}
                      dateFormat="dd/MM/yyyy"
                    />
                  </div>
                  {dueDateError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(dueDateError)}
                    </div>
                  )}
                </div>
                <div className="sm:col-end-3">
                  <FormLabel
                    htmlFor="reference"
                    label={t("Reference Number")}
                  />
                  <div className="mt-1">
                    <input
                      type="text"
                      name="reference"
                      id="reference"
                      placeholder={t("Enter Reference Number")}
                      className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full text-sm sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100"
                      value={reference}
                      onChange={(e) => setreference(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </form>
            <div className="flex flex-col">
              <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
                  <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                            <FormLabel
                              htmlFor="product"
                              label={t("Product")}
                              required
                            />
                          </th>
                          <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                            <FormLabel
                              htmlFor="quantity"
                              label={t("Quantity")}
                              required
                            />
                          </th>
                          <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                            <FormLabel
                              htmlFor="price"
                              label={t("Price")}
                              required
                            />
                          </th>
                          <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                            {t("Subtotal")}
                          </th>
                          <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                            <FormLabel htmlFor="tax" label={t("Taxes")} />
                          </th>
                          <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                            <FormLabel
                              htmlFor="dppType"
                              label={t("dppMethod")}
                            />
                          </th>
                          <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                            {t("invoices.fields.taxBase")}
                          </th>
                          <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                            {t("totalTax")}
                          </th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {invoiceItems?.map((item, index) => {
                          const { product, price, quantity } = item;

                          // Validation
                          const isProductValid =
                            product !== "" || Boolean(item.productID);
                          const isQuantityValid = quantity !== 0;
                          const isPriceValid = price !== "";

                          // One canonical breakdown so this row can never
                          // disagree with the invoice total below it.
                          const {
                            itemTotal: productSubtotal,
                            otherTaxBase: itemTaxBase,
                            // Net of anything withheld, so the line adds up to
                            // what is actually billed for it.
                            totalTax: itemTax,
                            taxes: lineTaxes,
                          } = calculateItemTaxBreakdown(
                            item,
                            subtotal,
                            discountAmount,
                            applyAfterTax,
                            taxDirectory,
                            taxRounding,
                          );

                          return (
                            <Fragment key={index}>
                              <tr key={index}>
                                <td className="px-4 py-2 font-medium text-sm text-gray-00 whitespace-nowrap min-w-[10rem]">
                                  {businessProductsError ? (
                                    <ErrorSelect
                                      name="product"
                                      customClassNames="mt-0"
                                      error={businessProductsError}
                                      onRefresh={() => {
                                        if (businessIdParam) {
                                          requestListBusinessProduct(
                                            businessProductDispatch,
                                            {
                                              page: 1,
                                              businessID: businessIdParam,
                                            },
                                          );
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Select
                                      id="product"
                                      className={classNames(
                                        !isProductValid && submitted
                                          ? "border-red-500 rounded border focus:border-red-500 focus:ring-red-500"
                                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                                        "text-sm",
                                      )}
                                      classNamePrefix="select2-selection"
                                      options={businessProducts.map((item) => ({
                                        ...item,
                                        value: item.id,
                                        label: item.name,
                                      }))}
                                      value={
                                        item?.product ||
                                        (item?.productID
                                          ? {
                                              value: item.productID,
                                              label: t("products.unavailable"),
                                            }
                                          : "")
                                      }
                                      onChange={(selectedOption) =>
                                        handleProductChange(
                                          index,
                                          selectedOption,
                                        )
                                      }
                                      placeholder={t("Select Product")}
                                      maxMenuHeight={150}
                                      menuPortalTarget={document.body}
                                      isDisabled={businessProductsLoading}
                                      isLoading={businessProductsLoading}
                                    />
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap w-20">
                                  <input
                                    type="number"
                                    name="quantity"
                                    value={item.quantity || ""}
                                    max={999}
                                    maxLength={3}
                                    placeholder={t("Enter quantity")}
                                    onChange={(e) =>
                                      handleQuantityChange(e, index)
                                    }
                                    className={classNames(
                                      !isQuantityValid && submitted
                                        ? "border-red-500 rounded border focus:border-red-500 focus:ring-red-500"
                                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                                      "block w-full text-sm rounded-md shadow-sm",
                                    )}
                                  />
                                </td>
                                <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap min-w-[10rem]">
                                  <NumericFormat
                                    type="text"
                                    name="price"
                                    id="price"
                                    value={item.price?.value || ""}
                                    placeholder={t("Enter Price")}
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    displayType="input"
                                    prefix={"Rp"}
                                    onValueChange={(values) => {
                                      setinvoiceItems(
                                        updateInvoiceItem(index, {
                                          price: values,
                                        }),
                                      );
                                    }}
                                    className={classNames(
                                      !isPriceValid && submitted
                                        ? "border-red-500 rounded border focus:border-red-500 focus:ring-red-500"
                                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                                      "block w-full text-sm rounded-md shadow-sm",
                                    )}
                                  />
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap text-right">
                                  <NumericFormat
                                    type="text"
                                    name="subtotal"
                                    id="subtotal"
                                    value={productSubtotal}
                                    {...senDecimalProps(productSubtotal)}
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    displayType="text"
                                    prefix={"Rp"}
                                    className="block w-full text-sm"
                                  />
                                </td>
                                <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap min-w-[10rem]">
                                  <ItemTaxPicker
                                    id={`tax-${index}`}
                                    options={availableTaxes}
                                    value={item?.selectedTaxes}
                                    isLoading={businessTaxesLoading}
                                    error={businessTaxesError}
                                    onRefresh={() => {
                                      if (businessIdParam) {
                                        requestListBusinessTax(
                                          businessTaxDispatch,
                                          {
                                            page: 1,
                                            businessID: businessIdParam,
                                          },
                                        );
                                      }
                                    }}
                                    onChange={(next) =>
                                      handleTaxesChange(index, next)
                                    }
                                  />
                                </td>
                                <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap min-w-[10rem]">
                                  <div className="flex flex-col gap-2">
                                    <Select
                                      id="taxBaseType"
                                      className="text-sm"
                                      classNamePrefix="select2-selection"
                                      options={taxBaseTypes}
                                      value={item.tax?.taxBaseType || null}
                                      onChange={(selectedOption) =>
                                        setinvoiceItems(
                                          updateInvoiceItem(index, {
                                            tax: {
                                              ...item.tax,
                                              taxBaseType: selectedOption,
                                              taxBaseRate: null,
                                              taxBasePercentage: null,
                                              taxBaseFractionNumerator: null,
                                              taxBaseFractionDenumerator: null,
                                            },
                                          }),
                                        )
                                      }
                                      placeholder={t("selectDPPMethod")}
                                      maxMenuHeight={150}
                                      menuPortalTarget={document.body}
                                      isDisabled={!item.tax?.value}
                                      isClearable
                                    />
                                    {Boolean(item.tax?.taxBaseType) &&
                                      renderTaxBaseForm(item, index)}
                                  </div>
                                </td>
                                <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                  <NumericFormat
                                    // DPP is a VAT figure; a line that only
                                    // has PPh withheld has none.
                                    value={
                                      lineTaxes.some((entry) => !entry.withholding)
                                        ? itemTaxBase
                                        : "-"
                                    }
                                    {...senDecimalProps(lineTaxes.some((entry) => !entry.withholding) ? itemTaxBase : "-")}
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    displayType="text"
                                    prefix={"Rp"}
                                    className="block w-full text-sm"
                                  />
                                </td>
                                <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                  <NumericFormat
                                    value={itemTax}
                                    {...senDecimalProps(itemTax)}
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    displayType="text"
                                    prefix={"Rp"}
                                    className="block w-full text-sm"
                                  />
                                </td>
                                <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                  <button
                                    onClick={() => handleRemoveRow(index)}
                                    className="flex items-center text-sm rounded-md shadow-sm border border-gray-300 px-4 py-2"
                                    disabled={invoiceItems.length === 1}
                                  >
                                    <TrashIcon
                                      className={`h-5 w-5 text-${
                                        invoiceItems.length === 1
                                          ? "gray-400 cursor-not-allowed"
                                          : "red-500 cursor-pointer"
                                      } `}
                                    />
                                  </button>
                                </td>
                              </tr>
                              {newInvoiceItemsError &&
                                newInvoiceItemsError[index] && (
                                  <tr>
                                    <td
                                      colSpan="9"
                                      className="px-4 py-2 text-sm text-left text-red-600"
                                    >
                                      {Object.entries(
                                        newInvoiceItemsError[index],
                                      ).map(([field, errors], errorIndex) => (
                                        <div
                                          key={`error-message-${errorIndex}`}
                                        >
                                          {errors.map((errorMessage, i) => (
                                            <div key={`error-message-${i}`}>
                                              {t(errorMessage)}
                                            </div>
                                          ))}
                                        </div>
                                      ))}
                                    </td>
                                  </tr>
                                )}
                            </Fragment>
                          );
                        })}
                        <tr>
                          <td
                            className="p-4 text-sm text-left text-gray-700 whitespace-nowrap"
                            colSpan={9}
                          >
                            <div className="flex gap-3 items-center justify-between">
                              <div className="flex gap-3 items-center">
                                <button
                                  type="button"
                                  onClick={handleAddRow}
                                  className="text-sm font-medium text-blue-600"
                                >
                                  {t("Add Product")}
                                </button>
                                <div className="border-r-2 border-gray-300" />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setshowProductModal(true);
                                    setproductType("GOODS");
                                    handleAddRow();
                                  }}
                                  className="text-sm font-medium text-blue-600"
                                >
                                  {t("Create Product")}
                                </button>
                                <div className="border-r-2 border-gray-300" />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setshowTaxModal(true);
                                    setcomputation("PERCENTAGE");
                                  }}
                                  className="text-sm font-medium text-blue-600"
                                >
                                  {t("Create Tax")}
                                </button>
                              </div>
                              <div className="flex space-x-6 items-center">
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    id="subtract-stock"
                                    checked={subtractStock}
                                    onChange={(e) => {
                                      setsubtractStock(e.target.checked);
                                      if (!e.target.checked) {
                                        setignoreStockValidation(false);
                                      }
                                    }}
                                    disabled={Boolean(transactionIdParam)}
                                    className={`h-4 w-4 border-gray-300 rounded ${
                                      transactionIdParam
                                        ? "opacity-60 cursor-not-allowed bg-gray-200"
                                        : "text-blue-600 focus:ring-blue-500"
                                    }`}
                                  />
                                  <label
                                    htmlFor="subtract-stock"
                                    className="ml-2 block text-sm text-gray-700"
                                  >
                                    {t(
                                      isPurchase
                                        ? "bills.table.headers.addStock"
                                        : "invoices.table.headers.subtractStock",
                                    )}
                                  </label>
                                </div>
                                {!transactionIdParam && (
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      id="ignore-stock-validation"
                                      checked={ignoreStockValidation}
                                      disabled={!subtractStock}
                                      onChange={(e) =>
                                        setignoreStockValidation(
                                          e.target.checked,
                                        )
                                      }
                                      className={`h-4 w-4 border-gray-300 rounded ${
                                        !subtractStock
                                          ? "opacity-60 cursor-not-allowed bg-gray-200"
                                          : "text-blue-600 focus:ring-blue-500"
                                      }`}
                                    />
                                    <label
                                      htmlFor="ignore-stock-validation"
                                      className={`ml-2 block text-sm ${
                                        !subtractStock
                                          ? "text-gray-500"
                                          : "text-gray-700"
                                      }`}
                                    >
                                      {t(
                                        "invoices.table.headers.ignoreStockValidation",
                                      )}
                                    </label>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 justify-between">
              <div className="flex flex-col col-span-2 gap-4">
                <div>
                  <FormLabel htmlFor="message" label={t("Discount")} />
                  <div className="flex mt-1 gap-4">
                    <div className="flex-1">
                      <Select
                        id="selctedDiscount"
                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
                        classNamePrefix="select2-selection"
                        options={discountType}
                        value={selectedDiscount}
                        onChange={onSelectDiscont}
                        placeholder={t("Select Discount")}
                        menuPortalTarget={document.body}
                      />
                    </div>
                    <div className="flex-1">
                      <div>
                        <div className="flex">
                          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                            {selectedDiscount?.value === "fixed" ? "Rp" : "%"}
                          </span>
                          <NumericFormat
                            type="text"
                            name="discount"
                            id="discount"
                            value={discount}
                            onChange={handleChangeDiscount}
                            disabled={selectedDiscount?.value === "noDiscount"}
                            placeholder={t("Enter Amount")}
                            thousandSeparator="."
                            decimalSeparator=","
                            className={classNames(
                              discountError
                                ? "border-red-500 rounded border focus:border-red-500 focus:ring-red-500"
                                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                              "flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed",
                            )}
                            displayType="input"
                          />
                        </div>
                        {discountError && (
                          <div className="mt-1 text-red-600 text-sm">
                            {t(discountError)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <FormLabel htmlFor="message" label={t("Message")} />
                  <div className="mt-1">
                    <input
                      type="text"
                      name="message"
                      id="message"
                      placeholder={t("Enter Message")}
                      className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full text-sm sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100"
                      value={description}
                      onChange={(e) => setdescription(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1 col-span-2 sm:col-span-1 text-sm sm:col-end-5">
                <div className="flex justify-between font-medium">
                  <span>{t("Subtotal")}</span>
                  <NumericFormat
                    type="text"
                    name="subtotal"
                    id="subtotal"
                    value={subtotal}
                    {...senDecimalProps(subtotal)}
                    thousandSeparator="."
                    decimalSeparator=","
                    displayType="text"
                    prefix={"Rp"}
                  />
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>{t("Discount")}</span>
                    <NumericFormat
                      value={-(discountAmount || 0)}
                      {...senDecimalProps(-(discountAmount || 0))}
                      thousandSeparator="."
                      decimalSeparator=","
                      displayType="text"
                      prefix={"Rp"}
                    />
                  </div>
                )}
                {taxes.map((tax) => {
                  // An unresolved tax is still charged, so the row needs a name
                  // even when getAllTaxes could not read one. A withheld tax is
                  // cut by the counterparty, so it reads as a deduction.
                  const row = describeTaxRow(tax, t);
                  return (
                    <div
                      key={tax.id}
                      className={clsx(
                        "flex justify-between gap-2 ",
                        row.withholding && "text-amber-700",
                      )}
                    >
                      <span>{row.label}</span>
                      <NumericFormat
                        type="text"
                        name="totalAmount"
                        id="totalAmount"
                        value={row.value}
                        {...senDecimalProps(row.value)}
                        thousandSeparator="."
                        decimalSeparator=","
                        displayType="text"
                        prefix={"Rp"}
                      />
                    </div>
                  );
                })}
                {roundingAmount ? (
                  <div className="flex justify-between gap-2">
                    <span>{t("invoices.fields.rounding")}</span>
                    <NumericFormat
                      value={roundingAmount}
                      {...senDecimalProps(roundingAmount)}
                      thousandSeparator="."
                      decimalSeparator=","
                      displayType="text"
                      prefix={"Rp"}
                    />
                  </div>
                ) : null}
                <Divider />
                <div className="flex justify-between font-medium">
                  <span>{t("Total")}</span>
                  <NumericFormat
                    type="text"
                    name="totalTransaction"
                    id="totalTransaction"
                    value={totalWithTax}
                    {...senDecimalProps(totalWithTax)}
                    thousandSeparator="."
                    decimalSeparator=","
                    displayType="text"
                    prefix={"Rp"}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-4 justify-end bg-gray-50 rounded-b-md sm:rounded-b-lg px-4 py-6 text-right sm:px-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              type="submit"
              className="w-full sm:w-auto"
              title={
                transactionIdParam ? t("Save Invoice") : t("Create Invoice")
              }
              loading={businessLoading || newInvoiceLoading || submitLoading}
              disabled={
                businessLoading ||
                businesses.length === 0 ||
                newInvoiceLoading ||
                submitLoading ||
                dateLocked ||
                isBusinessWriteBlocked(selectedBusiness)
              }
              onClick={(e) => {
                e.preventDefault();
                transactionIdParam ? onSave(true) : onSave();
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceCreate;
